import React, { useState } from 'react';
import { calculateReverse } from './pensionCalculator';
import KakaoAdBanner from './KakaoAdBanner';
import KrwAmountInput from './KrwAmountInput';

function ReverseCalculator({ showInlineMobileAd }) {
  const [formData, setFormData] = useState({
    current_age: 30,
    retirement_age: 60,
    initial_principal: 0,
    desired_monthly_income: 1250000,
    annual_return_rate: 7.0,
    pension_yield_rate: 4.0,
    life_expectancy_age: 100,
  });

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value === '' ? '' : parseFloat(value) || 0,
    }));
  };

  const handleAmountChange = (name) => (amount) => {
    setFormData((prev) => ({
      ...prev,
      [name]: amount,
    }));
  };

  const formatNumber = (num) =>
    num === null || num === undefined
      ? '계산 불가'
      : new Intl.NumberFormat('ko-KR').format(num);

  const MIN_DESIRED_MONTHLY_INCOME = 100000;

  const handleCalculate = (e) => {
    e.preventDefault();
    setError('');

    if (
      formData.desired_monthly_income == null ||
      formData.desired_monthly_income < MIN_DESIRED_MONTHLY_INCOME
    ) {
      setError(
        `원하는 노후 월 생활비는 최소 ${formatNumber(MIN_DESIRED_MONTHLY_INCOME)}원 이상이어야 합니다.`
      );
      return;
    }

    setLoading(true);
    try {
      const data = calculateReverse(formData);
      setResult(data);
    } catch (err) {
      setError('계산 중 오류가 발생했습니다: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Page hero */}
      <div className="lb-page-hero">
        <div className="lb-container">
          <p className="lb-eyebrow">Reverse Calculator</p>
          <h1 className="lb-title-lg">역산 계산기</h1>
          <p className="lb-body">원하는 노후 생활비에서 필요한 월 저축액을 계산합니다</p>
        </div>
      </div>

      {/* Main layout */}
      <section className="lb-section">
        <div className="lb-container">
          <div className="lb-calc-layout">

            {/* ── Input form ── */}
            <div>
              <div className="lb-card lb-card-padded">
                <h2 className="lb-title-md" style={{ marginBottom: '24px' }}>계산 조건 입력</h2>

                <form onSubmit={handleCalculate}>
                  <div className="lb-form-row">
                    <div className="lb-form-grid-2">
                      <div>
                        <label className="lb-label">현재 나이</label>
                        <input
                          type="number"
                          name="current_age"
                          value={formData.current_age}
                          onChange={handleInputChange}
                          className="lb-input"
                          min="20"
                          max="65"
                          required
                        />
                        <p className="lb-form-hint">만 나이</p>
                      </div>
                      <div>
                        <label className="lb-label">은퇴 나이</label>
                        <input
                          type="number"
                          name="retirement_age"
                          value={formData.retirement_age}
                          onChange={handleInputChange}
                          className="lb-input"
                          min="50"
                          max="80"
                          required
                        />
                        <p className="lb-form-hint">은퇴 예정 나이</p>
                      </div>
                    </div>
                  </div>

                  <div className="lb-form-row">
                    <label className="lb-label">초기 자금 (원)</label>
                    <KrwAmountInput
                      name="initial_principal"
                      value={formData.initial_principal}
                      onValueChange={handleAmountChange('initial_principal')}
                      className="lb-input"
                      step={1000000}
                    />
                    <p className="lb-form-hint">이미 보유한 자산 (없으면 0)</p>
                  </div>

                  <div className="lb-form-row">
                    <label className="lb-label">원하는 노후 월 생활비 (원)</label>
                    <KrwAmountInput
                      name="desired_monthly_income"
                      value={formData.desired_monthly_income}
                      onValueChange={handleAmountChange('desired_monthly_income')}
                      className="lb-input"
                      step={10000}
                      required
                    />
                    <p className="lb-form-hint">은퇴 후 매월 사용하고 싶은 금액</p>
                  </div>

                  <div className="lb-form-row">
                    <label className="lb-label">연 복리 수익률 (%)</label>
                    <input
                      type="number"
                      name="annual_return_rate"
                      value={formData.annual_return_rate}
                      onChange={handleInputChange}
                      className="lb-input"
                      min="0"
                      max="20"
                      step="0.1"
                      required
                    />
                    <p className="lb-form-hint">저축 기간 동안 예상 수익률</p>
                  </div>

                  <div className="lb-form-row">
                    <label className="lb-label">은퇴 후 포트폴리오 연 수익률 (%)</label>
                    <input
                      type="number"
                      name="pension_yield_rate"
                      value={formData.pension_yield_rate}
                      onChange={handleInputChange}
                      className="lb-input"
                      min="0"
                      max="10"
                      step="0.1"
                      required
                    />
                    <p className="lb-form-hint">원금 보존은 이자/배당만, 원금 소진은 원금과 수익을 함께 사용합니다</p>
                  </div>

                  <div className="lb-form-row">
                    <label className="lb-label">기대수명</label>
                    <input
                      type="number"
                      name="life_expectancy_age"
                      value={formData.life_expectancy_age}
                      onChange={handleInputChange}
                      className="lb-input"
                      min={Math.max(51, formData.retirement_age + 1)}
                      max="150"
                      step="1"
                      required
                    />
                    <p className="lb-form-hint">은퇴 후 목표 생활비를 받을 마지막 나이</p>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
                      {[100, 120, 150].map((age) => (
                        <button
                          key={age}
                          type="button"
                          className="lb-btn lb-btn-outline"
                          style={{ padding: '8px 12px', width: 'auto' }}
                          onClick={() => setFormData((prev) => ({ ...prev, life_expectancy_age: age }))}
                        >
                          {age}세
                        </button>
                      ))}
                    </div>
                  </div>

                  {error && (
                    <div className="lb-alert lb-alert-error">
                      ⚠️ {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="lb-btn lb-btn-primary"
                    style={{ width: '100%', marginTop: '8px' }}
                  >
                    {loading ? (
                      <><span className="lb-spinner" />계산 중...</>
                    ) : (
                      '🎯 필요 저축액 계산하기'
                    )}
                  </button>
                </form>
              </div>
            </div>

            {showInlineMobileAd && (
              <KakaoAdBanner className="calc-result-mobile-ad" placement="mobile" />
            )}

            {/* ── Results ── */}
            <div>
              {!result && (
                <div className="lb-card lb-card-padded" style={{ textAlign: 'center', padding: '56px 28px' }}>
                  <p style={{ fontSize: '40px', marginBottom: '16px' }}>🎯</p>
                  <p className="lb-subtitle" style={{ marginTop: 0 }}>계산 결과가 여기에 표시됩니다</p>
                  <p className="lb-card-desc">좌측 폼에서 조건을 입력하고 계산해보세요!</p>
                </div>
              )}

              {result && (
                <>
                  {/* Summary stat cards */}
                  <div className="lb-grid-3" style={{ marginBottom: '24px' }}>
                    <div className="lb-stat-card">
                      <div className="lb-stat-label">필요 최종 적립액</div>
                      <div className="lb-stat-value">
                        {formatNumber(result.required_final_amount)}
                        {result.interest_only_available && (
                          <span style={{ fontSize: '14px', fontWeight: '700' }}>원</span>
                        )}
                      </div>
                    </div>
                    <div className="lb-stat-card">
                      <div className="lb-stat-label">매월 저축해야 할 금액</div>
                      <div className="lb-stat-value lb-stat-value--accent">
                        {formatNumber(result.required_monthly_saving)}
                        {result.interest_only_available && (
                          <span style={{ fontSize: '14px', fontWeight: '700' }}>원</span>
                        )}
                      </div>
                    </div>
                    <div className="lb-stat-card">
                      <div className="lb-stat-label">원금 소진 필요 적립액</div>
                      <div className="lb-stat-value">
                        {formatNumber(result.depletion_required_final_amount)}
                        <span style={{ fontSize: '14px', fontWeight: '700' }}>원</span>
                      </div>
                    </div>
                  </div>

                  {!result.interest_only_available && (
                    <div className="lb-alert lb-alert-error" style={{ marginBottom: '24px' }}>
                      {result.interest_only_unavailable_reason}
                    </div>
                  )}

                  {/* Goal summary */}
                  <div className="lb-card lb-card-padded" style={{ marginBottom: '24px' }}>
                    <h3 className="lb-title-md" style={{ marginBottom: '16px' }}>📊 목표 설정</h3>
                    <div>
                      <div className="lb-detail-item">
                        <span className="lb-detail-label">저축 기간</span>
                        <span className="lb-detail-value">{result.total_payment_period}년</span>
                      </div>
                      <div className="lb-detail-item">
                        <span className="lb-detail-label">목표 월 생활비</span>
                        <span className="lb-detail-value lb-detail-value--accent">
                          {formatNumber(formData.desired_monthly_income)}원
                        </span>
                      </div>
                      <div className="lb-detail-item">
                        <span className="lb-detail-label">필요 최종 적립액</span>
                        <span className="lb-detail-value">
                          {formatNumber(result.required_final_amount)}
                          {result.interest_only_available && '원'}
                        </span>
                      </div>
                      <div className="lb-detail-item">
                        <span className="lb-detail-label">원금 소진 필요 적립액</span>
                        <span className="lb-detail-value lb-detail-value--accent">
                          {formatNumber(result.depletion_required_final_amount)}원
                        </span>
                      </div>
                      <div className="lb-detail-item">
                        <span className="lb-detail-label">원금 소진 수령 기간</span>
                        <span className="lb-detail-value">
                          {result.depletion_start_age}세~{result.depletion_end_age}세
                          ({result.depletion_withdrawal_years}년)
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Monthly saving details */}
                  <div className="lb-card lb-card-padded" style={{ marginBottom: '24px' }}>
                    <h3 className="lb-title-md" style={{ marginBottom: '16px' }}>📅 저축 계획</h3>
                    <div>
                      <div className="lb-detail-item">
                        <span className="lb-detail-label">원금 보존 기준 월 저축액</span>
                        <span className="lb-detail-value lb-detail-value--accent">
                          {formatNumber(result.required_monthly_saving)}
                          {result.interest_only_available && '원/월'}
                        </span>
                      </div>
                      <div className="lb-detail-item">
                        <span className="lb-detail-label">원금 소진 기준 월 저축액</span>
                        <span className="lb-detail-value lb-detail-value--accent">
                          {formatNumber(result.depletion_required_monthly_saving)}원/월
                        </span>
                      </div>
                      <div className="lb-detail-item">
                        <span className="lb-detail-label">원금 보존 총 저축액</span>
                        <span className="lb-detail-value">
                          {formatNumber(result.total_saving_amount)}
                          {result.interest_only_available && '원'}
                        </span>
                      </div>
                      <div className="lb-detail-item">
                        <span className="lb-detail-label">원금 소진 총 저축액</span>
                        <span className="lb-detail-value">{formatNumber(result.depletion_total_saving_amount)}원</span>
                      </div>
                      <div className="lb-detail-item">
                        <span className="lb-detail-label">원금 보존 예상 수익</span>
                        <span className="lb-detail-value lb-detail-value--accent">
                          {result.interest_only_available
                            ? `+${formatNumber(result.expected_interest_earned)}원`
                            : '계산 불가'}
                        </span>
                      </div>
                      {result.interest_only_available && (
                        <>
                          <div className="lb-detail-item">
                            <span className="lb-detail-label">필요 적립액 감소분</span>
                            <span className="lb-detail-value lb-detail-value--accent">
                              {formatNumber(result.required_final_amount_reduction)}원
                            </span>
                          </div>
                          <div className="lb-detail-item">
                            <span className="lb-detail-label">월 저축액 감소분</span>
                            <span className="lb-detail-value lb-detail-value--accent">
                              {formatNumber(result.monthly_saving_reduction)}원/월
                            </span>
                          </div>
                        </>
                      )}
                      <p className="lb-form-hint" style={{ marginTop: '14px' }}>
                        원금 소진 결과는 수익률이 일정하고 선택한 기대수명에 원금이 0원이 된다고 가정한 단순 계산입니다.
                      </p>
                    </div>
                  </div>

                  {/* Year-by-year progress */}
                  {result.interest_only_available && (
                    <div className="lb-card lb-card-padded">
                      <h3 className="lb-title-md" style={{ marginBottom: '16px' }}>📈 연도별 저축 진행</h3>
                      <div>
                        {result.savings_details.map((detail, index) => (
                          <div key={index} className="lb-detail-item">
                            <div>
                              <span className="lb-detail-label" style={{ fontWeight: '700', color: 'var(--color-brown-800)' }}>
                                {detail.year}년차
                              </span>
                              <span style={{ color: 'var(--color-text-muted)', marginLeft: '6px', fontSize: '13px' }}>
                                ({detail.age}세)
                              </span>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div className="lb-detail-value">
                                {formatNumber(detail.accumulated_amount)}원
                              </div>
                              <div style={{ fontSize: '12px', color: 'var(--color-brown-600)', marginTop: '2px' }}>
                                수익 +{formatNumber(detail.interest_earned)}원
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

          </div>
        </div>
      </section>

      {/* Info section */}
      <section className="lb-section-sm" style={{ background: 'rgba(250, 242, 232, 0.4)' }}>
        <div className="lb-container">
          <div className="lb-section-head">
            <p className="lb-eyebrow">How it works</p>
            <h2 className="lb-title-md">역산 계산 방식 설명</h2>
          </div>
          <div className="lb-grid-3">
            <div className="lb-card lb-card-padded">
              <div className="lb-card-icon">📊</div>
              <h3 className="lb-title-md">목표 설정</h3>
              <p className="lb-card-desc">원하는 노후 생활비를 먼저 정하고, 이를 위해 필요한 자금을 역산합니다.</p>
            </div>
            <div className="lb-card lb-card-padded">
              <div className="lb-card-icon">💰</div>
              <h3 className="lb-title-md">최종 적립액 계산</h3>
              <p className="lb-card-desc">원금 보존 방식과 기대수명까지 원금을 소진하는 방식을 함께 계산합니다.</p>
            </div>
            <div className="lb-card lb-card-padded">
              <div className="lb-card-icon">📈</div>
              <h3 className="lb-title-md">월 저축액 도출</h3>
              <p className="lb-card-desc">복리 수익을 고려하여 매월 저축해야 할 금액을 계산합니다.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ReverseCalculator;
